import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
} from '@mui/material';
import { CheckCircle as CheckIcon, AutoAwesome as AutoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '../../services/api';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const PricingSection = () => {
    // Auto-detect Currency: If timezone is India, default to INR
    const detectDefaultCurrency = () => {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timeZone === 'Asia/Calcutta' || timeZone === 'Asia/Kolkata') {
                return 'inr';
            }
        } catch (e) {
            // Ignore if Intl is not supported
        }
        return 'usd';
    };

    const [currency, setCurrency] = useState(detectDefaultCurrency());
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    const handleCurrencyChange = (event, newCurrency) => {
        if (newCurrency !== null) {
            setCurrency(newCurrency);
        }
    };

    const isINR = currency === 'inr';

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsLoggedIn(!!user);
        });

        const fetchPlans = async () => {
            try {
                // Step 1: Get a temporary backend-issued JWT
                const token = await billingApi.getPublicToken();

                // Step 2: Use the token to fetch sanitized plan config
                const data = await billingApi.getPublicConfig(token);
                if (data && data.plans) {
                    // Convert the object format from server to array format for rendering
                    const plansArray = Object.entries(data.plans)
                        .map(([key, value]) => ({ ...value, id: key }))
                        .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
                    setPlans(plansArray);
                }
            } catch (error) {
                console.error("Failed to fetch public plans:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
        return () => unsubscribe();
    }, []);

    const handleSelectPlan = (planId) => {
        if (!isLoggedIn) {
            // Redirect to login with a redirectTo parameter
            navigate(`/app/settings?redirectTo=/app/pricing`);
            // Note: Our RequireAuth inside /app will catch this and show LoginPage
            return;
        }
        navigate('/app/pricing');
    };

    return (
        <Box id="pricing" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ position: 'absolute', top: '20%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '40vh', background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 60%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        PRICING PLANS
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.5rem', md: '3.5rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2 }}>
                        Scale your social, not your costs.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', maxWidth: 540, mx: 'auto', lineHeight: 1.8, mb: 4 }}>
                        Save over 90% compared to legacy tools like Hootsuite and Sprout Social. Built exclusively on high-efficiency Gemini 1.5 AI.
                    </Typography>

                    {/* Currency Toggle */}
                    <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '100px', p: 0.5 }}>
                        <ToggleButtonGroup
                            value={currency}
                            exclusive
                            onChange={handleCurrencyChange}
                            aria-label="currency pricing"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 4, py: 1, borderRadius: '100px', color: 'rgba(255,255,255,0.5)', fontWeight: 700, border: 'none', textTransform: 'none',
                                    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }
                            }}
                        >
                            <ToggleButton value="usd" aria-label="usd pricing">Global (USD)</ToggleButton>
                            <ToggleButton value="inr" aria-label="inr pricing">India (INR)</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <Typography color="rgba(255,255,255,0.4)">Loading plans...</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${plans.length}, 1fr)` }, gap: 3, alignItems: 'stretch' }}>
                        {plans.map((plan, i) => {
                            const accent = plan.accent || '#8b5cf6';
                            const isPopular = !!plan.badge;
                            return (
                                <Box key={i} sx={{
                                    position: 'relative',
                                    display: 'flex', flexDirection: 'column',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    border: `1.5px solid ${isPopular ? `${accent}50` : `${accent}20`}`,
                                    background: `linear-gradient(145deg, ${accent}${isPopular ? '0c' : '06'} 0%, rgba(15,23,42,${isPopular ? '0.9' : '0.8'}) 100%)`,
                                    backdropFilter: 'blur(16px)',
                                    boxShadow: isPopular
                                        ? `0 20px 60px -10px ${accent}30, 0 0 80px -20px ${accent}20`
                                        : `0 16px 40px -8px rgba(0,0,0,0.4)`,
                                    transform: isPopular ? { md: 'translateY(-16px) scale(1.02)' } : 'none',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: isPopular ? { md: 'translateY(-20px) scale(1.03)' } : 'translateY(-4px)',
                                        boxShadow: `0 20px 60px -10px ${accent}35, 0 0 80px -20px ${accent}25`,
                                        borderColor: `${accent}50`,
                                    },
                                }}>
                                    {/* Ambient glow blob */}
                                    <Box sx={{
                                        position: 'absolute', top: -50, right: -50,
                                        width: 140, height: 140, borderRadius: '50%',
                                        background: `radial-gradient(circle, ${accent}${isPopular ? '20' : '12'} 0%, transparent 70%)`,
                                        pointerEvents: 'none',
                                    }} />

                                    {/* Badge */}
                                    {isPopular && (
                                        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
                                            <Box sx={{
                                                display: 'flex', justifyContent: 'center', pt: 0,
                                            }}>
                                                <Chip label={plan.badge} size="small" sx={{
                                                    bgcolor: accent, color: '#fff', fontWeight: 800,
                                                    fontSize: '0.65rem', letterSpacing: '0.1em',
                                                    borderRadius: '0 0 8px 8px',
                                                    height: 24,
                                                    boxShadow: `0 4px 12px ${accent}40`,
                                                }} />
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                                        <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: 'white', mb: 0.5, lineHeight: 1.2 }}>
                                            {plan.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem', color: `${accent}aa`, fontWeight: 600, mb: 3 }}>
                                            {plan.target}
                                        </Typography>

                                        {/* Price */}
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 3 }}>
                                            <Typography sx={{ fontWeight: 900, fontSize: '2.8rem', color: accent, lineHeight: 1 }}>
                                                {isINR ? (plan.price?.inr || 'N/A') : (plan.price?.usd || 'N/A')}
                                            </Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.9rem' }}>
                                                {plan.period}
                                            </Typography>
                                        </Box>

                                        {/* Divider */}
                                        <Box sx={{ height: '1px', bgcolor: `${accent}15`, mb: 2.5 }} />

                                        {/* Features */}
                                        <Stack spacing={1.5} sx={{ mb: 4, flex: 1 }}>
                                            {(isINR ? (plan.features_ind || []) : (plan.features_global || [])).map((feature, idx) => {
                                                const isAI = feature.includes('Gemini');
                                                return (
                                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                                        {isAI ? (
                                                            <AutoIcon sx={{ color: accent, fontSize: 16, flexShrink: 0, mt: 0.2 }} />
                                                        ) : (
                                                            <CheckIcon sx={{ color: `${accent}80`, fontSize: 16, flexShrink: 0, mt: 0.2 }} />
                                                        )}
                                                        <Typography sx={{
                                                            color: isAI ? 'white' : 'rgba(255,255,255,0.65)',
                                                            fontWeight: isAI ? 700 : 500,
                                                            fontSize: '0.85rem', lineHeight: 1.5,
                                                        }}>
                                                            {feature}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>

                                        {/* CTA Button */}
                                        <Button
                                            onClick={() => handleSelectPlan(plan.id)}
                                            variant="contained"
                                            fullWidth
                                            sx={{
                                                py: 1.5,
                                                borderRadius: '12px',
                                                fontWeight: 800,
                                                fontSize: '0.95rem',
                                                color: isPopular ? '#000' : 'white',
                                                bgcolor: isPopular ? accent : `${accent}18`,
                                                border: `1.5px solid ${accent}${isPopular ? '' : '30'}`,
                                                boxShadow: isPopular ? `0 4px 20px ${accent}40` : 'none',
                                                '&:hover': {
                                                    bgcolor: isPopular ? '#fff' : `${accent}25`,
                                                    boxShadow: `0 8px 24px ${accent}30`,
                                                    transform: 'translateY(-1px)',
                                                },
                                                transition: 'all 0.25s ease',
                                            }}
                                        >
                                            {plan.name === 'Solo Strategist' ? 'Start Free' : `Get ${plan.name}`}
                                        </Button>
                                    </Box>

                                    {/* Bottom accent bar */}
                                    <Box sx={{
                                        height: 3,
                                        background: `linear-gradient(90deg, ${accent}, ${accent}40)`,
                                    }} />
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default PricingSection;
